/// Tidal heights — terminology, datums, and the relationships
/// between charted depth, drying height, height of tide, and what
/// the sounder reads under the keel. Pure data; the SVG diagram
/// lives in apps/web.
///
/// Source: Admiralty Tide Tables conventions + IHO S-44 standards
/// for chart datums. Modern Admiralty charts use LAT as Chart Datum
/// (CD = LAT). Older charts may use other datums — check the
/// chart's title block.

// ─── Vertical datums (the horizontal lines on the diagram) ───────

export interface TidalLevel {
  abbreviation: string;
  name: string;
  description: string;
  /// Rank from highest (HAT = 0) to lowest (LAT = 7) so the legend
  /// can be rendered consistently from top to bottom.
  rank: number;
}

export const TIDAL_LEVELS: readonly TidalLevel[] = [
  {
    abbreviation: "HAT",
    name: "Highest Astronomical Tide",
    description:
      "The highest level predictable under any combination of astronomical conditions — no meteorological surge. Bridges and other vertical-clearance features on charts are normally measured from HAT to give a conservative air draft.",
    rank: 0,
  },
  {
    abbreviation: "MHWS",
    name: "Mean High Water Springs",
    description:
      "Average of the two HW heights on each of the days of full + new moons (spring tides) over a long period. The 'normal high' for springs.",
    rank: 1,
  },
  {
    abbreviation: "MHWN",
    name: "Mean High Water Neaps",
    description:
      "Average HW height on days of half-moons (neap tides). Smaller swing than springs.",
    rank: 2,
  },
  {
    abbreviation: "ML",
    name: "Mean Level",
    description:
      "Average of all observed heights at the station — close to but not identical to Mean Sea Level (MSL).",
    rank: 3,
  },
  {
    abbreviation: "MLWN",
    name: "Mean Low Water Neaps",
    description:
      "Average LW height on neap-tide days. The shallow end of a neap tide.",
    rank: 4,
  },
  {
    abbreviation: "MLWS",
    name: "Mean Low Water Springs",
    description:
      "Average LW height on spring-tide days. Significantly lower than MLWN — uncovers drying features on the chart.",
    rank: 5,
  },
  {
    abbreviation: "LAT",
    name: "Lowest Astronomical Tide",
    description:
      "Lowest level predictable under any astronomical combination. The modern Admiralty Chart Datum — depths and drying heights are referenced to this. A 0.0 m tidal height means the tide is exactly at LAT.",
    rank: 6,
  },
  {
    abbreviation: "CD",
    name: "Chart Datum",
    description:
      "The reference zero on the chart. Modern Admiralty charts: CD = LAT. Older charts may use MLWS or another local datum — check the chart's title block.",
    rank: 7,
  },
];

// ─── Measurements (the labelled arrows on the diagram) ───────────

export interface TidalMeasurement {
  id: string;
  name: string;
  abbreviation: string | null;
  /// What this measurement is, plain English.
  definition: string;
  /// Where it's measured from / to.
  reference: string;
  /// How it appears on the chart, or where you read it.
  shownOnChart: string;
}

export const TIDAL_MEASUREMENTS: readonly TidalMeasurement[] = [
  {
    id: "charted-depth",
    name: "Charted depth",
    abbreviation: null,
    definition:
      "Depth of the seabed below Chart Datum. The number printed in the open water on a chart — always a positive number meaning 'metres BELOW the datum'.",
    reference: "Chart Datum → seabed (positive downward).",
    shownOnChart:
      "Plain number in metres + tenths (e.g. 7.4) in open water. White / blue background.",
  },
  {
    id: "drying-height",
    name: "Drying height",
    abbreviation: null,
    definition:
      "Height of a feature (rock, sandbank, mud) ABOVE Chart Datum that dries at low water. The feature is underwater at most tides but exposed at LAT.",
    reference: "Chart Datum → top of feature (positive upward).",
    shownOnChart:
      "Underlined number on a green / yellow chart tint, e.g. 1.3 (the underline distinguishes it from a depth).",
  },
  {
    id: "height-of-tide",
    name: "Height of tide",
    abbreviation: "HoT",
    definition:
      "Vertical distance from Chart Datum up to the actual water surface at a given moment. The tidal-prediction tables give this number directly.",
    reference: "Chart Datum → current water surface.",
    shownOnChart:
      "Read from tide tables / tidal-curve calculation, not the chart itself. Always added to charted depth to find actual sounding.",
  },
  {
    id: "sounding",
    name: "Sounding (actual depth)",
    abbreviation: null,
    definition:
      "Real water depth at a position — what your echo sounder reads. Equals charted depth + height of tide.",
    reference: "Current water surface → seabed.",
    shownOnChart:
      "Not on the chart. Read the echo sounder, or compute: sounding = charted depth + height of tide.",
  },
  {
    id: "range",
    name: "Tidal range",
    abbreviation: null,
    definition:
      "Vertical difference between consecutive HW and LW heights. Springs range = MHWS − MLWS; neaps range = MHWN − MLWN; for a specific day it's the actual HW height − LW height from the table.",
    reference: "HW → LW height (or vice versa).",
    shownOnChart:
      "Read from the tide table for the day; springs/neaps ranges may be quoted in the almanac for each port.",
  },
  {
    id: "feature-height",
    name: "Height of feature",
    abbreviation: null,
    definition:
      "Elevation of a charted feature (bridge, lighthouse, conspicuous object) above a reference datum — usually HAT or MHWS. Lights have their elevation marked next to the light symbol.",
    reference: "HAT or MHWS → top of feature (chart-dependent; check the title block).",
    shownOnChart:
      "Plain metres next to the symbol, often inside brackets: '(15)' for a 15 m elevation. Bridges show their MHWS clearance as a vertical-clearance number.",
  },
  {
    id: "vertical-clearance",
    name: "Vertical clearance / air draft",
    abbreviation: null,
    definition:
      "Free space between the actual water surface and the underside of an overhead obstruction (bridge, power cable). Critical for sailing yachts at high water.",
    reference: "Current water surface → underside of feature.",
    shownOnChart:
      "Chart shows clearance at HAT (most conservative); convert via height of tide: clearance now = chart clearance + (HAT − height of tide).",
  },
];

export const TIDAL_MEASUREMENT_IDS: readonly string[] =
  TIDAL_MEASUREMENTS.map((m) => m.id);
